/**
 * @module ol-ishare/lang
 */

import { buildUrl, checkHttpStatusIsOk } from './http.js';

let lookup = {};

/**
 * Returns a Promise which resolves to an object containing iShare language
 * strings in the format:
 *
 * ```json
 * {
 *   "oli": {
 *       "baseMapsTitle": "Base maps",
 *       "deviceLocation": {
 *           "showLocation": "Show your location"
 *       },
 *       "fullScreen": {
 *           "tipLabel": "Toggle full-screen"
 *       },
 *       "gazetteer": {
 *           "noResults": "No results found",
 *           "placeholder": "Search...",
 *           "queryTooShort": "Type ${minQueryLength} or more characters for results",
 *           "selectedOption": "${selectedOption} ${index} of ${length} is highlighted",
 *           "resultsDescription": "Result count: ${length}. ${selectedOption}",
 *           "assistiveHint": "When autocomplete results are available, use the up and down arrows to review and enter to select. Users of touch devices can explore by touch or with swipe gestures."
 *       },
 *       "layerSwitcher": {
 *           "hideButtonTitle": "Collapse legend",
 *           "showButtonTitle": "Show legend"
 *       },
 *       "historyNavigation": {
 *           "backHtml": "&lArr;",
 *           "backTitle": "Navigate back",
 *           "forwardHtml": "&rArr;",
 *           "forwardTitle": "Navigate forward"
 *       },
 *       "zoom": {
 *           "zoomInTipLabel": "Zoom in",
 *           "zoomOutTipLabel": "Zoom out"
 *       },
 *       "apps": {
 *           "spotlight": {
 *               "baseMapsTitle": "Base layer",
 *               "gazetteer": {
 *                   "placeholder": "Partial address or postcode..."
 *               }
 *           }
 *       }
 *   }
 * }
 * ```
 *
 * @param {String} iShareUrl Base URL for iShare Web app
 * @returns {Promise<Object>} Promise which resolves to object of iShare
 * language strings
 */
function getLang(iShareUrl) {
  const url = buildUrl(iShareUrl, 'atJSlangSettings.aspx', { format: 'json' });
  return fetch(url.toString())
    .then(checkHttpStatusIsOk)
    .then(function (resp) {
      return resp.json();
    })
    .then(function (obj) {
      // Return the object containing the actual string
      return obj.lang || {};
    })
    .catch(function (err) {
      // Return an empty object if we are unable to fetch the language strings as
      // JSON from iShare (iShare < v6.0.9)
      console.warn(`Unable to load language strings from ${url}\n\n`, err);
      return {};
    })
    .then(function (_lookup) {
      lookup = _lookup;
      return _lookup;
    });
}

export {
  getLang,
  /**
   * Language string lookup for example:
   * ```json
   * {
   *   "oli": {
   *       "baseMapsTitle": "Base maps",
   *       "deviceLocation": {
   *           "showLocation": "Show your location"
   *       },
   *       ...
   *   }
   * }
   * ```
   * @type Object
   */
  lookup as lang
};
